// iter119 — Modal obligatoire de sélection de l'intervenant principal au démarrage de l'OR.
// Affichée quand on tente de passer en 'commencement' (ou travaux_effectue/pret) sans principal_id.
import { useState, useMemo } from "react";
import { UserCheck, X, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function PrincipalSelectDialog({ open, mecaniciens = [], onCancel, onConfirm, targetStatut }) {
  const [selectedId, setSelectedId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sorted = useMemo(() => [...mecaniciens].sort((a, b) => (a.label || "").localeCompare(b.label || "")), [mecaniciens]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await onConfirm(selectedId);
    } finally {
      setSubmitting(false);
      setSelectedId("");
    }
  };

  const handleCancel = () => {
    setSelectedId("");
    onCancel?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="principal-select-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-[#52a675]" />
            Démarrer l&apos;OR — Choisir l&apos;intervenant principal
          </DialogTitle>
          <DialogDescription>
            Pour passer en <b>{targetStatut === "commencement" ? "« Commencement »" : `« ${targetStatut} »`}</b>, sélectionne le mécanicien principal de l&apos;OR. Tu pourras le modifier plus tard dans la fiche.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1" data-testid="principal-list">
          {sorted.length === 0 && (
            <div className="text-sm text-gray-500 italic py-4 text-center">
              Aucun mécanicien disponible. Ajoute un intervenant dans Paramètres &gt; Atelier.
            </div>
          )}
          {sorted.map((m) => {
            const active = selectedId === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedId(m.id)}
                data-testid={`principal-option-${m.id}`}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  active
                    ? "border-[#52a675] bg-[#52a675]/10 shadow-sm"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${active ? "bg-[#52a675] text-white" : "bg-gray-100 text-gray-700"}`}>
                  {(m.label || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{m.label}</div>
                  {m.source === "user_legacy" && (
                    <div className="text-[10px] text-gray-400">via compte utilisateur</div>
                  )}
                </div>
                {active && <UserCheck className="w-5 h-5 text-[#52a675]" />}
              </button>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={submitting} data-testid="principal-cancel">
            <X className="w-4 h-4 mr-1" /> Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedId || submitting}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="principal-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1" />}
            Démarrer l&apos;OR
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
